import React, { useState } from 'react';
import { bool, func, object, string } from 'prop-types';

import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_EMAIL_VERIFICATION_REQUIRED_MESSAGE } from '../../util/listingEmailVerification';
import Button from '../Button/Button';

import css from './VerifyEmailBeforeListingModal.module.css';

const noop = () => null;

const MailIcon = () => (
  <svg className={css.mailIcon} viewBox="0 0 28 28" aria-hidden="true" focusable="false">
    <rect x="4" y="7" width="20" height="15" rx="2" />
    <path d="M5 8l9 7 9-7" />
  </svg>
);

const AlertIcon = () => (
  <svg className={css.alertIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4.75v4" />
    <path d="M8 11.5h.01" />
  </svg>
);

const VerifyEmailBeforeListingModal = props => {
  const {
    id = 'VerifyEmailBeforeListingModal',
    isOpen = false,
    sendVerificationEmailInProgress = false,
    sendVerificationEmailError = null,
    onResendVerificationEmail,
    onGoBack = noop,
    onRefresh,
  } = props;
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [localResendError, setLocalResendError] = useState(false);

  const handleResendVerificationEmail = () => {
    if (!onResendVerificationEmail || sendVerificationEmailInProgress) {
      return null;
    }

    setVerificationEmailSent(false);
    setLocalResendError(false);

    return Promise.resolve(onResendVerificationEmail())
      .then(() => {
        setVerificationEmailSent(true);
      })
      .catch(() => {
        setVerificationEmailSent(false);
        setLocalResendError(true);
      });
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  };

  const showError = Boolean(sendVerificationEmailError || localResendError);
  const showSuccess = verificationEmailSent && !showError;

  return isOpen ? (
    <section className={css.root}>
      <div
        id={id}
        className={css.modalContainer}
        role="dialog"
        aria-modal="false"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-message`}
      >
        <div className={css.iconBadge}>
          <MailIcon />
        </div>

        <h2 id={`${id}-title`} className={css.title}>
          <FormattedMessage
            id="VerifyEmailBeforeListingModal.title"
            defaultMessage="Verify your email before listing"
          />
        </h2>

        <p id={`${id}-message`} className={css.message}>
          <FormattedMessage
            id="VerifyEmailBeforeListingModal.message"
            defaultMessage={LISTING_EMAIL_VERIFICATION_REQUIRED_MESSAGE}
          />
        </p>

        {showError ? (
          <div className={css.alert} role="alert">
            <AlertIcon />
            <p className={css.alertText}>
              <FormattedMessage
                id="VerifyEmailBeforeListingModal.resendError"
                defaultMessage="We could not send the verification email. Please try again."
              />
            </p>
          </div>
        ) : null}

        {showSuccess ? (
          <div className={css.success} role="status">
            <p className={css.successText}>
              <FormattedMessage
                id="VerifyEmailBeforeListingModal.resendSuccess"
                defaultMessage="Verification email sent. Please check your inbox."
              />
            </p>
          </div>
        ) : null}

        <p className={css.refreshText}>
          <FormattedMessage
            id="VerifyEmailBeforeListingModal.refreshPrefix"
            defaultMessage="Already verified? "
          />
          <button className={css.refreshButton} type="button" onClick={handleRefresh}>
            <FormattedMessage
              id="VerifyEmailBeforeListingModal.refreshCta"
              defaultMessage="Refresh the page"
            />
          </button>
          <FormattedMessage
            id="VerifyEmailBeforeListingModal.refreshSuffix"
            defaultMessage=" and try again."
          />
        </p>

        <div className={css.actions}>
          <Button
            rootClassName={css.primaryButton}
            spinnerClassName={css.primaryButtonSpinner}
            type="button"
            aria-label="Resend verification email"
            inProgress={sendVerificationEmailInProgress}
            disabled={sendVerificationEmailInProgress || !onResendVerificationEmail}
            onClick={handleResendVerificationEmail}
          >
            <FormattedMessage
              id="VerifyEmailBeforeListingModal.resendCta"
              defaultMessage="Resend verification email"
            />
          </Button>

          <Button rootClassName={css.backButton} type="button" onClick={onGoBack}>
            <FormattedMessage id="VerifyEmailBeforeListingModal.goBack" defaultMessage="Go back" />
          </Button>
        </div>
      </div>
    </section>
  ) : null;
};

VerifyEmailBeforeListingModal.propTypes = {
  id: string,
  isOpen: bool,
  currentUser: object,
  sendVerificationEmailInProgress: bool,
  sendVerificationEmailError: object,
  onResendVerificationEmail: func,
  onGoBack: func,
  onRefresh: func,
};

export default VerifyEmailBeforeListingModal;
